import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Routes, Route } from 'react-router-dom';
import { generateClientMessage } from '../../utils/gemini';
import ImagePickerField from './ImagePickerField';

const OfferDetailsView = ({
  offers,
  navigate,
  editOffer,
  copyToClipboard
}) => {
  const { id: viewId } = useParams();
  const selectedOffer = offers.find(o => o.firestoreId === viewId);
  
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [generatingMessage, setGeneratingMessage] = useState(false);
  
  if (!selectedOffer) return <div className="p-12 text-center text-black font-medium font-sans">Ponuda nije pronađena ili se učitava...</div>;

  const publicUrl = window.location.origin + '/client/' + selectedOffer.id;
  
  const calculateTotalFinal = () => {
    let total = parseFloat(selectedOffer.cijena) || 0;
    if (selectedOffer.clientSelectedExtras) {
      selectedOffer.clientSelectedExtras.forEach(extra => {
        total += parseFloat(extra.cijena) || 0;
      });
    }
    if (selectedOffer.privacyRequested && selectedOffer.privacyOption?.enabled) {
      total += parseFloat(selectedOffer.privacyOption.price) || 0;
    }
    return total;
  };

  const handleGenerateMessage = async () => {
    setGeneratingMessage(true);
    try {
      const message = await generateClientMessage(selectedOffer);
      setGeneratedMessage(message);
      setShowMessageModal(true);
    } catch (error) {
      console.error('Error generating message:', error);
      alert('Greška pri generisanju poruke. Pokušajte ponovo.');
    } finally {
      setGeneratingMessage(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-8 max-w-4xl mx-auto border border-gray-100 font-sans">
      <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
        <button 
          onClick={() => navigate('/admin/offers')} 
          className="w-fit flex items-center gap-1.5 text-black font-bold uppercase tracking-[0.2em] text-[10px] transition-all bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 hover:bg-black hover:text-white"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
          </svg>
          Nazad
        </button>
        
        <div className="flex items-baseline gap-3 min-w-0">
          <h2 className="text-xl md:text-2xl font-black text-black tracking-tight whitespace-nowrap">Pregled Ponude</h2>
          <span className="text-[9px] text-black font-mono uppercase tracking-widest hidden sm:inline truncate opacity-60"># {selectedOffer.id}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <label className="text-[9px] font-bold text-black uppercase tracking-widest block mb-1">KLIJENT</label>
              <div className="text-lg font-bold text-black leading-tight">{selectedOffer.name}</div>
            </div>
            <span className={`status-badge status-${selectedOffer.status} px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider`}>
              {selectedOffer.status}
            </span>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <label className="text-[9px] font-bold text-black uppercase tracking-widest block mb-2">OSNOVNI PAKET</label>
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-black text-sm">{selectedOffer.nazivPaketa}</span>
              <span className="font-black text-black">{selectedOffer.cijena} {selectedOffer.valuta}</span>
            </div>
            {selectedOffer.opis && (
              <p className="text-[11px] text-black italic leading-relaxed border-t border-gray-200 pt-2">
                {typeof selectedOffer.opis === 'object' ? (selectedOffer.opis.bs || selectedOffer.opis.en) : selectedOffer.opis}
              </p>
            )}
          </div>

          {(selectedOffer.clientSelectedExtras?.length > 0 || selectedOffer.privacyRequested) && (
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-black uppercase tracking-widest block">ODABRANI DODACI</label>
              {selectedOffer.clientSelectedExtras?.map((extra, idx) => (
                <div key={idx} className="flex justify-between items-center text-xs">
                  <span className="text-black font-medium">• {typeof extra.naziv === 'object' ? extra.naziv.bs : extra.naziv}</span>
                  <span className="font-bold text-black">+{extra.cijena} {extra.valuta}</span>
                </div>
              ))}
              {selectedOffer.privacyRequested && (
                 <div className="flex justify-between items-center text-xs text-emerald-700">
                   <span className="font-medium">🔒 Diskrecija / Privatnost</span>
                   <span className="font-bold">+{selectedOffer.privacyOption?.price} {selectedOffer.privacyOption?.valuta}</span>
                 </div>
              )}
            </div>
          )}

          <div className="p-5 bg-black rounded-xl text-white flex justify-between items-center">
             <div>
                <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white mb-1">UKUPNA INVESTICIJA</div>
                <div className="text-[10px] text-white/70 italic font-medium">Uračunati dodaci</div>
             </div>
             <div className="text-2xl font-black">
                {calculateTotalFinal()} <span className="text-xs font-medium text-white uppercase">{selectedOffer.valuta}</span>
             </div>
          </div>

          {selectedOffer.clientNotes && (
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
              <label className="text-[9px] font-bold text-amber-600 uppercase tracking-widest block mb-2">NAPOMENE KLIJENTA</label>
              <div className="text-xs text-amber-900 font-medium leading-relaxed whitespace-pre-wrap">
                {selectedOffer.clientNotes}
              </div>
            </div>
          )}

          {selectedOffer.status === 'odbijeno' && selectedOffer.rejectionReason && (
            <div className="bg-red-50 p-4 rounded-xl border border-red-100">
              <label className="text-[9px] font-bold text-red-600 uppercase tracking-widest block mb-2">RAZLOG ODBIJANJA</label>
              <div className="text-xs text-red-900 font-medium leading-relaxed whitespace-pre-wrap">
                {selectedOffer.rejectionReason}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <label className="text-[9px] font-bold text-black uppercase tracking-widest block mb-3 font-mono">JAVNI LINK ZA KLIJENTA</label>
            <div className="space-y-3">
              <input 
                type="text" 
                readOnly 
                value={publicUrl}
                className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[11px] text-black font-mono focus:outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => copyToClipboard(publicUrl)}
                  className="bg-black text-white py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
                >
                  Kopiraj link
                </button>
                <a 
                  href={publicUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#1a4d2e] text-white py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-black transition-all text-center flex items-center justify-center gap-2"
                >
                  Otvori ponudu
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-2 pt-4 border-t border-gray-100">
            <button 
              onClick={() => { editOffer(selectedOffer); navigate(`/admin/offers/edit/${selectedOffer.firestoreId}`); }} 
              className="w-full bg-black text-white py-3 rounded-lg font-bold uppercase tracking-widest text-[10px] hover:bg-gray-800 transition-all"
            >
              Uredi osnovnu ponudu
            </button>
            <button 
              onClick={handleGenerateMessage}
              disabled={generatingMessage}
              className="w-full bg-[#1a4d2e] text-white py-3 rounded-lg font-bold uppercase tracking-widest text-[10px] hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {generatingMessage ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generisanje...
                </>
              ) : (
                'Kreiraj poruku za klijenta'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-black">Generisana poruka za klijenta</h3>
                <button 
                  onClick={() => setShowMessageModal(false)}
                  className="text-black hover:text-black transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <pre className="whitespace-pre-wrap text-sm text-black font-mono leading-relaxed">
                  {generatedMessage}
                </pre>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    copyToClipboard(generatedMessage);
                    setShowMessageModal(false);
                  }}
                  className="flex-1 bg-black text-white py-2.5 rounded-lg font-bold uppercase tracking-widest text-[10px] hover:bg-gray-800 transition-all"
                >
                  Kopiraj poruku
                </button>
                <button 
                  onClick={() => setShowMessageModal(false)}
                  className="flex-1 bg-gray-300 text-black py-2.5 rounded-lg font-bold uppercase tracking-widest text-[10px] hover:bg-gray-400 transition-all font-sans"
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OfferListView = ({
  offers,
  navigate,
  setEditingId,
  setNewOffer,
  loading,
  editOffer,
  deleteOffer,
  getOfferTotal
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredOffers = offers.filter(offer => {
    const nameStr = offer.name || '';
    const pkgStr = offer.nazivPaketa || '';
    const matchesSearch = nameStr.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         pkgStr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || offer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusGlow = (status) => {
    switch (status) {
      case 'draft': return 'shadow-lg shadow-gray-200 border-gray-200';
      case 'poslano': return 'shadow-lg shadow-amber-500/20 border-amber-200';
      case 'prihvaćeno': return 'shadow-lg shadow-emerald-500/20 border-emerald-200';
      case 'odbijeno': return 'shadow-lg shadow-red-500/20 border-red-200';
      default: return 'shadow-sm border-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin')} 
            className="w-fit flex items-center gap-1.5 text-black font-bold uppercase tracking-[0.2em] text-[10px] transition-all bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 hover:bg-black hover:text-white"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
            Nazad
          </button>
          <h2 className="text-xl font-black text-black tracking-tight">Lista Ponuda</h2>
        </div>
        
        <button 
          className="w-fit bg-black text-white px-5 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-gray-800 transition-all font-sans" 
          onClick={() => {
            setEditingId(null);
            setNewOffer({
              name: '', datum: '', nazivPaketa: '', opis: { bs: '', en: '' }, trajanje: { bs: '', en: '' }, cijena: '', valuta: 'BAM', status: 'draft', prikaziNaNaslovnoj: false, id: '', slikaDesktop: 'hero1d.jpg', slikaMobile: 'hero1d.jpg', email: '', phone: '', message: '', usluge: [], extraOffers: [], privacyOption: { enabled: false, price: '150', valuta: 'BAM' }, displayLanguage: 'bs'
            });
            navigate('/admin/offers/new');
          }}
        >
          + Nova Ponuda
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-black">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Pretraži klijenta ili paket..."
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:bg-white focus:border-black transition-all outline-none text-black"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-48 px-4 py-2 bg-gray-50 border border-gray-100 rounded-lg text-xs font-bold uppercase tracking-widest cursor-pointer focus:bg-white focus:ring-1 focus:ring-black transition-all text-black"
        >
          <option value="all">Svi Statusi</option>
          <option value="draft">Draft</option>
          <option value="poslano">Poslano</option>
          <option value="prihvaćeno">Prihvaćeno</option>
          <option value="odbijeno">Odbijeno</option>
        </select>
      </div>

      {loading ? <p className="text-black text-center py-12">Učitavanje...</p> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOffers.map((offer) => (
              <div 
                key={offer.firestoreId} 
                className={`bg-white rounded-xl p-5 border transition-all group hover:border-black ${getStatusGlow(offer.status)}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="min-w-0">
                    <h3 className="font-black text-black text-lg leading-tight truncate">{offer.name}</h3>
                    <p className="text-[10px] text-black font-bold uppercase tracking-widest mt-1 italic">{offer.datum}</p>
                  </div>
                  <span className={`status-badge status-${offer.status} px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest`}>
                    {offer.status}
                  </span>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-[9px] font-bold text-black uppercase tracking-widest block mb-1">PAKET</label>
                    <p className="text-xs text-black font-medium line-clamp-1">{offer.nazivPaketa}</p>
                  </div>
                  
                  {offer.status === 'odbijeno' && offer.rejectionReason && (
                    <div className="bg-red-50/50 p-2 rounded-lg border border-red-100">
                       <label className="text-[9px] font-bold text-red-600 uppercase tracking-widest block mb-0.5">RAZLOG ODBIJANJA</label>
                       <p className="text-[10px] text-red-700 font-medium line-clamp-2 italic leading-tight">"{offer.rejectionReason}"</p>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-end">
                    <div>
                      <label className="text-[9px] font-bold text-black uppercase tracking-widest block mb-1">CIJENA</label>
                      <div className="font-black text-black text-base">
                        {getOfferTotal(offer)} {offer.valuta}
                        {(offer.clientSelectedExtras?.length > 0 || offer.privacyRequested) && (
                          <span className="ml-1.5 text-[10px] text-emerald-600 tracking-tighter cursor-help" title="Cijena uključuje dodatne opcije">+ dodaci</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => navigate(`/admin/offers/view/${offer.firestoreId}`)} 
                    className="flex-1 px-3 py-2 bg-white text-black border border-gray-100 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all shadow-sm"
                  >
                    Pogledaj
                  </button>
                  <button 
                    onClick={() => { editOffer(offer); navigate(`/admin/offers/edit/${offer.firestoreId}`); }} 
                    className="flex-1 px-3 py-2 bg-white text-black border border-gray-100 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all shadow-sm"
                  >
                    Uredi
                  </button>
                  <button 
                    onClick={() => deleteOffer(offer.firestoreId)} 
                    className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-400 rounded-lg hover:bg-black hover:text-white transition-all border border-transparent"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredOffers.length === 0 && (
            <div className="text-center py-20 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <p className="text-black text-sm font-medium">Nema pronađenih ponuda prema zadanim kriterijima.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const OfferFormView = ({
  editingId,
  cancelEdit,
  packages,
  applyPackageToOffer,
  setNewOffer,
  newOffer,
  handleFormSubmitLocal,
  handleOfferInputChange,
  autoResize,
  handleNameBlur,
  currentServiceBs,
  setCurrentServiceBs,
  currentServiceEn,
  setCurrentServiceEn,
  addServiceToOffer,
  removeServiceFromOffer,
  extraOffers,
  copyToClipboard
}) => {
  return (
    <div className="max-w-4xl mx-auto font-sans">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={cancelEdit} 
            className="w-fit flex items-center gap-1.5 text-black font-bold uppercase tracking-[0.2em] text-[10px] transition-all bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 hover:bg-black hover:text-white"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
            Nazad
          </button>
          <h3 className="text-xl font-black text-black tracking-tight">{editingId ? 'Uredi ponudu' : 'Dodaj novu ponudu'}</h3>
        </div>
        
        {!editingId && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="text-[10px] font-bold text-black uppercase tracking-widest mb-3 block">BRZI ODABIR IZ PAKETA:</label>
            <div className="flex flex-wrap gap-2">
              {packages.map((pkg) => (
                <button 
                  key={pkg.firestoreId} 
                  type="button" 
                  className="px-3 py-1.5 bg-white border border-gray-200 text-black rounded-lg text-xs font-bold hover:border-black transition-all"
                  onClick={() => applyPackageToOffer(pkg)}
                >
                  {typeof pkg.naziv === 'object' ? pkg.naziv.bs : pkg.naziv}
                </button>
              ))}
              <button 
                  type="button" 
                  className="px-3 py-1.5 bg-white border border-gray-200 text-black rounded-lg text-xs font-bold hover:border-black transition-all"
                  onClick={() => setNewOffer(prev => ({ ...prev, nazivPaketa: '', cijena: '' }))}
              >
                  + Prazno
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleFormSubmitLocal} className="space-y-4">
           {/* Language Toggle */}
           <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between mb-4">
            <span className="text-[10px] font-bold text-black uppercase tracking-widest block">JEZIK PRIKAZA</span>
            <div className="flex bg-white border border-gray-200 rounded-lg p-1">
              {['bs', 'en'].map(lang => (
                <button 
                  key={lang}
                  type="button"
                  onClick={() => setNewOffer(prev => ({ ...prev, displayLanguage: lang }))}
                  className={`px-4 py-1.5 rounded-md text-[10px] font-bold transition-all uppercase ${newOffer.displayLanguage === lang ? 'bg-black text-white' : 'text-black hover:bg-gray-100'}`}
                >
                  {lang === 'bs' ? 'Bosanski' : 'English'}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Ime Klijenta (Naslov)</label>
            <textarea name="name" rows="1" className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black focus:border-black font-bold" value={newOffer.name} onChange={(e) => { handleOfferInputChange(e); autoResize(e); }} onBlur={handleNameBlur} required placeholder="npr. Nejira & Jasmin" />
          </div>

          <div className="form-group">
            <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Naziv Paketa</label>
            <textarea name="nazivPaketa" rows="1" className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black focus:border-black font-bold" value={newOffer.nazivPaketa} onChange={(e) => { handleOfferInputChange(e); autoResize(e); }} required placeholder="npr. Premium Paket" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Opis Paketa (BS)</label>
              <textarea name="opis.bs" rows="2" className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black focus:border-black font-medium" value={newOffer.opis?.bs || ''} onChange={(e) => { handleOfferInputChange(e); autoResize(e); }} placeholder="Opis na bosanskom..." />
            </div>
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Opis Paketa (EN)</label>
              <textarea name="opis.en" rows="2" className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black focus:border-black font-medium" value={newOffer.opis?.en || ''} onChange={(e) => { handleOfferInputChange(e); autoResize(e); }} placeholder="Description in English..." />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Trajanje (BS)</label>
              <div className="relative">
                <input name="trajanje.bs" value={newOffer.trajanje?.bs || ''} onChange={handleOfferInputChange} className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black" />
              </div>
            </div>
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Trajanje (EN)</label>
              <input name="trajanje.en" value={newOffer.trajanje?.en || ''} onChange={handleOfferInputChange} className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Cijena</label>
              <input name="cijena" value={newOffer.cijena} onChange={handleOfferInputChange} required className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black font-bold" />
            </div>
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Valuta</label>
              <select name="valuta" value={newOffer.valuta} onChange={handleOfferInputChange} className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black font-bold">
                <option value="BAM">BAM</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Status</label>
              <select name="status" value={newOffer.status} onChange={handleOfferInputChange} className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black">
                <option value="draft">Draft</option>
                <option value="poslano">Poslano</option>
                <option value="prihvaćeno">Prihvaćeno</option>
                <option value="odbijeno">Odbijeno</option>
              </select>
            </div>
            <div className="form-group">
              <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">Datum Vjenčanja</label>
              <input type="date" name="datum" value={newOffer.datum} onChange={handleOfferInputChange} required className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black" />
            </div>
          </div>

          <div className="form-group pt-4 border-t border-gray-50">
            <label className="block text-xs font-bold text-black uppercase mb-2 tracking-widest">Sadržaj ponude</label>
            <div className="flex flex-col gap-2">
              <textarea rows="1" value={currentServiceBs} onChange={(e) => { setCurrentServiceBs(e.target.value); autoResize(e); }} onKeyDown={addServiceToOffer} placeholder="Stavka na bosanskom..." className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black text-black" />
              <textarea rows="1" value={currentServiceEn} onChange={(e) => { setCurrentServiceEn(e.target.value); autoResize(e); }} onKeyDown={addServiceToOffer} placeholder="Item in English..." className="w-full rounded-lg border-gray-200 text-sm p-3 border focus:ring-black text-black" />
              <button type="button" onClick={(e) => addServiceToOffer(e)} className="w-full bg-gray-100 text-black py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest">+ Dodaj stavku</button>
            </div>
            <ul className="mt-4 space-y-2">
              {newOffer.usluge?.map((service, index) => (
                <li key={index} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg text-xs font-medium">
                  <span className="text-black">{typeof service === 'object' ? `${service.bs} / ${service.en || ''}` : service}</span>
                  <span onClick={() => removeServiceFromOffer(index)} className="text-black hover:text-red-500 cursor-pointer font-bold px-2">×</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="form-group bg-gray-50 p-4 rounded-xl border border-gray-100">
            <label className="text-[10px] font-bold text-black uppercase tracking-widest mb-3 block">DODATNE PONUDE (ADD-ONS):</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {extraOffers?.map((extra) => {
                const isSelected = newOffer.extraOffers?.some(e => e.firestoreId === extra.firestoreId);
                return (
                    <label 
                    key={extra.firestoreId} 
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      isSelected 
                        ? 'bg-[#bfa46d] border-[#bfa46d] text-white shadow-md' 
                        : 'bg-white border-gray-200 text-black hover:border-[#bfa46d]'
                    }`}
                    onClick={() => {
                      const currentExtras = newOffer.extraOffers || [];
                      const exists = currentExtras.some(e => e.firestoreId === extra.firestoreId);
                      if (exists) {
                        setNewOffer(prev => ({
                          ...prev,
                          extraOffers: currentExtras.filter(e => e.firestoreId !== extra.firestoreId)
                        }));
                      } else {
                        setNewOffer(prev => ({
                          ...prev,
                          extraOffers: [...currentExtras, extra]
                        }));
                      }
                    }}
                  >
                    <div className="flex items-center justify-between pointer-events-none w-full">
                      <div className="flex items-center gap-3">
                          <div 
                          className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'text-white border-white bg-white' : 'text-[#bfa46d] border-gray-300'
                          }`}
                          >
                          </div>
                          <span className={`text-xs font-bold leading-tight ${isSelected ? 'text-white' : 'text-black'}`}>{typeof extra.naziv === 'object' ? extra.naziv.bs : extra.naziv}</span>
                      </div>
                      <span className={`text-[10px] font-bold ${isSelected ? 'text-white/80' : 'text-emerald-600'}`}>+{extra.cijena} {extra.valuta}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

           <div className="form-group">
               <div 
              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                newOffer.privacyOption?.enabled 
                  ? 'bg-[#bfa46d] border-[#bfa46d] text-white shadow-md' 
                  : 'bg-white border-gray-100 hover:border-[#bfa46d]'
              }`}
              onClick={() => setNewOffer(prev => ({
                ...prev, 
                privacyOption: { ...prev.privacyOption, enabled: !prev.privacyOption?.enabled }
              }))}
            >
              <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center transition-colors ${newOffer.privacyOption?.enabled ? 'bg-white border-white' : 'bg-white border-gray-300'}`}>
                {newOffer.privacyOption?.enabled && <div className="w-2 h-2 bg-[#bfa46d] rounded-full"></div>}
              </div>
              <div className="flex flex-col">
                <span className={`text-[10px] font-bold uppercase tracking-widest leading-none mb-1 ${
                  newOffer.privacyOption?.enabled ? 'text-white' : 'text-black'
                }`}>Prikaži opciju</span>
                <span className="text-sm font-bold leading-tight">DISKRECIJA / PRIVATNOST</span>
                <span className={`text-xs ${newOffer.privacyOption?.enabled ? 'text-white/80' : 'text-black'}`}>
                  Zabrana objave materijala na društvenim mrežama
                </span>
              </div>
            </div>
            
            {newOffer.privacyOption?.enabled && (
              <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex gap-4">
                <div className="form-group flex-1">
                  <label className="text-[10px] font-bold text-black uppercase tracking-widest mb-1 block">CIJENA ZA DISKRECIJU</label>
                  <input 
                    type="text" 
                    value={newOffer.privacyOption?.price || '150'} 
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setNewOffer(prev => ({
                      ...prev,
                      privacyOption: { ...prev.privacyOption, price: e.target.value }
                    }))}
                    placeholder="npr. 150"
                    className="w-full rounded-md border-gray-200 focus:border-black focus:ring-0 sm:text-sm p-3 border font-bold text-black"
                  />
                </div>
                <div className="form-group w-32">
                  <label className="text-[10px] font-bold text-black uppercase tracking-widest mb-1 block">VALUTA</label>
                  <select 
                    value={newOffer.privacyOption?.valuta || 'BAM'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setNewOffer(prev => ({
                      ...prev,
                      privacyOption: { ...prev.privacyOption, valuta: e.target.value }
                    }))}
                    className="w-full rounded-md border-gray-200 focus:border-black focus:ring-0 sm:text-sm p-3 border font-bold text-black"
                  >
                    <option value="BAM">BAM</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            )}
           </div>

           <div className="form-group">
            <label className="block text-xs font-bold text-black uppercase mb-1 tracking-widest">URL ID (jedinstveni link)</label>
            <input name="id" value={newOffer.id} onChange={handleOfferInputChange} required className="w-full rounded-lg border-gray-200 text-[11px] p-3 border focus:ring-black font-mono" />
          </div>

           <div className="form-group pb-6 border-b border-gray-50">
            <ImagePickerField 
              label="Hero Slika (Pozadina ponude)"
              value={newOffer.slikaDesktop}
              onSelect={(path) => setNewOffer(prev => ({ ...prev, slikaDesktop: path, slikaMobile: path }))}
            />
          </div>

          <div className="flex flex-wrap gap-3 pt-6 border-t border-gray-100">
            <button type="submit" className="px-12 py-4 bg-black text-white hover:bg-gray-800 rounded-xl font-bold uppercase tracking-[0.2em] text-[10px] transition-all shadow-lg flex-1 md:flex-none">
              {editingId ? 'Sačuvaj izmjene' : 'Kreiraj ponudu'}
            </button>
            <button type="button" onClick={cancelEdit} className="px-8 py-4 border-2 border-gray-100 text-black rounded-xl font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-gray-50 transition-all flex-1 md:flex-none">
              Otkaži
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminOffers = ({ 
  editingId, 
  packages, 
  extraOffers, 
  newOffer, 
  setNewOffer, 
  handleOfferSubmit, 
  handleOfferInputChange, 
  applyPackageToOffer,
  currentServiceBs,
  setCurrentServiceBs,
  currentServiceEn,
  setCurrentServiceEn,
  addServiceToOffer,
  removeServiceFromOffer,
  offers,
  editOffer,
  deleteOffer,
  loading,
  setEditingId
}) => {
  const navigate = useNavigate();

  // URL Sync Effect
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/edit/') && !editingId && offers.length > 0) {
      const idFromPath = path.split('/edit/')[1];
      const offerToEdit = offers.find(o => o.firestoreId === idFromPath);
      if (offerToEdit) editOffer(offerToEdit);
    }

    // Auto-resize all textareas on mount if in edit/new mode
    if (window.location.pathname.includes('/offers/new') || window.location.pathname.includes('/offers/edit')) {
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(ta => {
          ta.style.height = 'auto';
          ta.style.height = ta.scrollHeight + 'px';
        });
      }, 100);
    }
  }, [offers, editingId, editOffer]);

  const autoResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const generateSlug = (name, date) => {
    if (!name) return '';
    let slug = name.toLowerCase()
      .replace(/&/g, 'i')
      .replace(/[^a-z0-9 ]/g, '')
      .trim()
      .replace(/\s+/g, '-');
    
    const exists = offers.some(o => o.id === slug && o.firestoreId !== editingId);
    
    if (exists && date) {
      const year = new Date(date).getFullYear();
      if (!isNaN(year)) {
        slug = `${slug}-${year}`;
      }
    }
    
    return slug;
  };

  const handleNameBlur = () => {
    if (!newOffer.id && newOffer.name) {
      const suggestedSlug = generateSlug(newOffer.name, newOffer.datum);
      setNewOffer(prev => ({ ...prev, id: suggestedSlug }));
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Kopirano!');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Kopirano!');
    }
  };

  const handleFormSubmitLocal = async (e) => {
    await handleOfferSubmit(e);
    navigate('/admin/offers');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setNewOffer({
      name: '', datum: '', nazivPaketa: '', opis: { bs: '', en: '' }, cijena: '', valuta: 'BAM', status: 'draft', prikaziNaNaslovnoj: false, id: '', slikaDesktop: 'hero1d.jpg', slikaMobile: 'hero1d.jpg', email: '', phone: '', message: '', usluge: [], extraOffers: [], privacyOption: { enabled: false, price: '150', valuta: 'BAM' }, displayLanguage: 'bs'
    });
    navigate('/admin/offers');
  };

  const getOfferTotal = (offer) => {
    let total = parseFloat(offer.cijena) || 0;
    if (offer.clientSelectedExtras) {
      offer.clientSelectedExtras.forEach(extra => {
        total += parseFloat(extra.cijena) || 0;
      });
    }
    if (offer.privacyRequested && offer.privacyOption?.enabled) {
      total += parseFloat(offer.privacyOption.price) || 0;
    }
    return total;
  };

  return (
    <Routes>
      <Route path="/" element={<OfferListView offers={offers} navigate={navigate} setEditingId={setEditingId} setNewOffer={setNewOffer} loading={loading} editOffer={editOffer} deleteOffer={deleteOffer} getOfferTotal={getOfferTotal} />} />
      <Route path="/new" element={<OfferFormView editingId={editingId} cancelEdit={cancelEdit} packages={packages} applyPackageToOffer={applyPackageToOffer} setNewOffer={setNewOffer} newOffer={newOffer} handleFormSubmitLocal={handleFormSubmitLocal} handleOfferInputChange={handleOfferInputChange} autoResize={autoResize} handleNameBlur={handleNameBlur} currentServiceBs={currentServiceBs} setCurrentServiceBs={setCurrentServiceBs} currentServiceEn={currentServiceEn} setCurrentServiceEn={setCurrentServiceEn} addServiceToOffer={addServiceToOffer} removeServiceFromOffer={removeServiceFromOffer} extraOffers={extraOffers} copyToClipboard={copyToClipboard} />} />
      <Route path="/edit/:id" element={<OfferFormView editingId={editingId} cancelEdit={cancelEdit} packages={packages} applyPackageToOffer={applyPackageToOffer} setNewOffer={setNewOffer} newOffer={newOffer} handleFormSubmitLocal={handleFormSubmitLocal} handleOfferInputChange={handleOfferInputChange} autoResize={autoResize} handleNameBlur={handleNameBlur} currentServiceBs={currentServiceBs} setCurrentServiceBs={setCurrentServiceBs} currentServiceEn={currentServiceEn} setCurrentServiceEn={setCurrentServiceEn} addServiceToOffer={addServiceToOffer} removeServiceFromOffer={removeServiceFromOffer} extraOffers={extraOffers} copyToClipboard={copyToClipboard} />} />
      <Route path="/view/:id" element={<OfferDetailsView offers={offers} navigate={navigate} editOffer={editOffer} copyToClipboard={copyToClipboard} />} />
    </Routes>
  );
};

export default AdminOffers;
